import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/utils/api';

/** @param {import("prop-types").InferProps<typeof StepPaymentPropTypes>} props */
export default function StepPayment({
  formData,
  eventId,
  chargeId = null,
  setChargeId,
  paypalOrderId = null,
  setPaypalOrderId,
  setConfirmationInfo,
  paymentStatus = null,
  setPaymentStatus,
  errorMessage = '',
  setErrorMessage,
  isPaymentComplete,
  setIsPaymentComplete,
  isCreatingCharge,
  setIsCreatingCharge,
  prevStep,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en_US' : 'fr_CA';
  const chargeCreated = useRef(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Reset chargeCreated ref on unmount so re-entering this step triggers a fresh charge
  useEffect(
    () => () => {
      chargeCreated.current = false;
    },
    [],
  );

  useEffect(() => {
    const createCharge = async () => {
      if (chargeId || chargeCreated.current) return;

      try {
        chargeCreated.current = true;
        setIsCreatingCharge(true);
        const response = await api.post('/api/v2/charges/create/', {
          ...formData,
          event: eventId,
          currency: 'CAD',
        });

        setChargeId(response.data.chargeId);
        setPaypalOrderId(response.data.paypalOrderId);
      } catch (error) {
        setPaymentStatus('error');
        const data = error.response?.data;
        if (data?.errorKey && data?.fieldKeys?.length) {
          const fields = data.fieldKeys.map(k => t(k)).join(', ');
          setErrorMessage(t(data.errorKey, { field: fields }));
        } else {
          setErrorMessage(data?.message || t('donation.error.create'));
        }
      } finally {
        setIsCreatingCharge(false);
      }
    };

    createCharge();
  }, [
    chargeId,
    isCreatingCharge,
    formData,
    eventId,
    setChargeId,
    setPaypalOrderId,
    setPaymentStatus,
    setErrorMessage,
    setIsCreatingCharge,
    t,
  ]);

  const handleCapture = async () => {
    try {
      const response = await api.post('/api/v2/charges/capture/', {
        orderId: paypalOrderId,
        chargeId,
        event: eventId,
        ...formData,
      });

      if (response.data.success) {
        setPaymentStatus('success');
        setConfirmationInfo(response.data.donation);
      } else {
        setPaymentStatus('error');
        setErrorMessage(response.data.message || t('donation.error.paymentFailed'));
      }
    } catch (error) {
      setPaymentStatus('error');
      setErrorMessage(error.response?.data?.message || t('donation.error.unexpected'));
    } finally {
      setIsPaymentComplete(true);
      setIsProcessingPayment(false);
    }
  };

  return (
    <Box>
      <Typography gutterBottom variant="h5">
        {t('donation.pay')}
      </Typography>

      {paymentStatus === 'error' && (
        <Alert severity="error" sx={{ my: 2 }}>
          {errorMessage}
          {!isPaymentComplete && (
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              {t('donation.error.goBackHint')}
            </Typography>
          )}
        </Alert>
      )}

      {isCreatingCharge && (
        <Box>
          <Typography variant="body1">{t('donation.loading')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
            <CircularProgress />
          </Box>
        </Box>
      )}

      {!isPaymentComplete && paypalOrderId && (
        // Wrap the PayPal buttons in a Box for styling
        <Box sx={{ padding: 6, backgroundColor: '#ddd' }}>
          <PayPalScriptProvider
            options={{
              clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
              currency: 'CAD',
              locale,
            }}
          >
            {isProcessingPayment ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <Typography variant="body1" sx={{ marginBottom: 2 }}>
                  {t('donation.processing')}
                </Typography>
                <CircularProgress />
              </Box>
            ) : (
              <PayPalButtons
                createOrder={async () => paypalOrderId}
                onApprove={async () => {
                  setIsProcessingPayment(true);
                  await handleCapture();
                }}
                onError={() => {
                  setPaymentStatus('error');
                  setErrorMessage(t('donation.error.unexpected'));
                  setIsProcessingPayment(false);
                }}
                onCancel={() => {
                  setIsProcessingPayment(false);
                }}
              />
            )}
          </PayPalScriptProvider>
        </Box>
      )}

      <Box sx={{ marginTop: 4 }}>
        <Button
          variant={paymentStatus === 'error' ? 'contained' : 'outlined'}
          onClick={prevStep}
          disabled={isProcessingPayment}
        >
          {t('common.back')}
        </Button>
      </Box>
    </Box>
  );
}

const StepPaymentPropTypes = {
  chargeId: PropTypes.number,
  errorMessage: PropTypes.string,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formData: PropTypes.object.isRequired,
  isCreatingCharge: PropTypes.bool.isRequired,
  isPaymentComplete: PropTypes.bool.isRequired,
  paymentStatus: PropTypes.string,
  paypalOrderId: PropTypes.string,
  prevStep: PropTypes.func.isRequired,
  setChargeId: PropTypes.func.isRequired,
  setConfirmationInfo: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setIsCreatingCharge: PropTypes.func.isRequired,
  setIsPaymentComplete: PropTypes.func.isRequired,
  setPaymentStatus: PropTypes.func.isRequired,
  setPaypalOrderId: PropTypes.func.isRequired,
};
StepPayment.propTypes = StepPaymentPropTypes;
